import { LinkProps } from "@tanstack/react-router";
import { ActivityType } from "enums/ActivityType.enum";
import { InspectionStatus } from "enums/InspectionStatus.enum";
import { getSeparatorDetailedLabel } from "types/separator/separator.helpers";
import { Separator } from "types/separator/separator.type";
import { TransportSedelStatus } from "types/transportSedel.type";
import { Uppdrag } from "types/uppdrag.type";
import { WaterMeasurementProtocolStatus } from "types/waterMeasurementProtocol/waterMeasurementProtocol.helpers";
import { UppdragPageTab } from "../UppdragPage";

export type ActivityItem = {
  key: string;
  type: ActivityType;
  separator?: Separator;
  link: Pick<LinkProps, "to" | "params" | "search">;
  inspectionStatus?: InspectionStatus;
  transportSedelStatus?: TransportSedelStatus;
  waterMeasurementStatus?: WaterMeasurementProtocolStatus;
};

export type ActivitySeparatorGroup = {
  separator: Separator;
  items: ActivityItem[];
};

export const isActivityDone = (item: ActivityItem) =>
  item.inspectionStatus === InspectionStatus.RequirementsFulfilled ||
  item.transportSedelStatus === TransportSedelStatus.Completed ||
  item.waterMeasurementStatus === WaterMeasurementProtocolStatus.ReportCompiled;

// Builds the flat activity list for an uppdrag (six-month and five-year
// inspections + tömningar + provtagningar) and groups it the way the operator
// views expect: items without an object first, then grouped per separator
// (sorted by label).
export const buildUppdragActivities = (uppdrag: Uppdrag) => {
  const items: ActivityItem[] = [
    ...uppdrag.sixMonthsInspectionList.map(
      (x): ActivityItem => ({
        key: `6mån-${x.id}`,
        type: ActivityType.SixMonthsInspection,
        separator: { ...x.separator, facility: uppdrag.facility },
        link: {
          to: "/app/uppdrag/$uuid/sixm/$sixmid",
          params: { uuid: uppdrag.uuid, sixmid: x.id.toString() },
        },
        inspectionStatus: x.status,
      }),
    ),
    ...uppdrag.fiveYearsInspectionList.map(
      (x): ActivityItem => ({
        key: `5år-${x.id}`,
        type: ActivityType.FiveYearInspection,
        separator: { ...x.separator, facility: uppdrag.facility },
        link: {
          to: "/app/uppdrag/$uuid/fivey/$fiveyid",
          params: { uuid: uppdrag.uuid, fiveyid: x.id.toString() },
        },
        inspectionStatus: x.status,
      }),
    ),
    ...uppdrag.wasteList.map(
      (x): ActivityItem => ({
        key: `töm-${x.id}`,
        type: ActivityType.Tomning,
        separator: !x.tomning?.separator
          ? undefined
          : { ...x.tomning.separator, facility: uppdrag.facility },
        link: {
          to: "/app/uppdrag/$uuid",
          params: { uuid: uppdrag.uuid },
          search: { tab: UppdragPageTab.TransportSedel },
        },
        transportSedelStatus: x.transportSedel?.status,
      }),
    ),
    ...uppdrag.waterMeasurementProtocolList.map(
      (x): ActivityItem => ({
        key: `prov-${x.id}`,
        type: ActivityType.WaterMeasurementProtocol,
        separator: { ...x.separator, facility: uppdrag.facility },
        link: {
          to: "/app/uppdrag/$uuid/provtagning/$pid",
          params: { uuid: uppdrag.uuid, pid: x.id.toString() },
        },
        waterMeasurementStatus: x.status,
      }),
    ),
  ];

  const itemsWithoutSeparator = items.filter((x) => !x.separator);

  const groupMap = new Map<number, ActivitySeparatorGroup>();
  for (const item of items) {
    if (!item.separator) continue;
    const group = groupMap.get(item.separator.id) ?? {
      separator: item.separator,
      items: [],
    };
    group.items.push(item);
    groupMap.set(item.separator.id, group);
  }
  const separatorGroups = [...groupMap.values()].sort((a, b) =>
    getSeparatorDetailedLabel(a.separator) <
    getSeparatorDetailedLabel(b.separator)
      ? -1
      : 1,
  );

  return { items, itemsWithoutSeparator, separatorGroups };
};
